import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import * as _ from '../lodash';
import Modal from '../components/Modal';
import Select from '../components/Common/Select';
import RatesTable from '../components/Common/RatesTable';
import Table from '../components/Common/Table';
import TimeZone from '../components/Common/TimeZone';
import Button from '../components/Common/Button';
import DatePicker from '../components/DatePicker';
import Alert from '../components/Alert';
import Spinner from '../components/Spinner';
import { parseInt, capitalize, formatAvailabilityMessage } from '../utilities/helperUtility';
import { SHORT_DATE_FORMAT } from '../constants';
import { formatHours, formatPrice, getTimeFrameFromDate, convertDateFormat } from '../utilities/timeUtility';
import Translation from '../utilities/localizationUtility';
import ExceededMessage from "../components/exceeded_free_trial_message.js"
import Price from './components/Price';
import BookingFieldsBlock from './components/BookingFieldsBlock.jsx'
import { stringToBoolean } from '../utilities/conversionUtility';
import { customSelectStyles } from '../utilities/helperUtility';
import LocationSelect from './components/LocationSelect';

const firstSelectableDate = (frames, availableDates) => {
  const dates = frames.map(x => x.date);
  const today = new Date();
  let firstBookableDate = _.first(_.filter(dates, x => moment(x).diff(today, 'days') >= 0));
  if (firstBookableDate) {
    if (!availableDates.includes(firstBookableDate)) {
      firstBookableDate = availableDates[0];
    }
    return firstBookableDate;
  }
  return null;
};

const parseAvailableDates = (frames, totalQuantity, minDate, maxDate, timeZone, locationId) => {
  const today = moment.tz(timeZone);
  return frames.reduce((arr, frame) => {
    const theDate = moment.tz(frame.date, timeZone);
    let isValid = false;
    if (theDate.isSameOrAfter(today.clone().startOf('day'))) {
      const frameLocationIds = frame.locationIds || [];
      if (!locationId || frameLocationIds.includes(locationId)) isValid = true;
    }
    if (isValid && _.find(frame.timeslots, x => moment.tz(x.start, timeZone).isSameOrAfter(today))) {
      arr.push(frame.date);
    }
    return arr;
  }, []);
};

const parseSelectOptions = (frames, selectedDate, maxAvailability, totalQuantity, capacityType, locale, timeZone) => {
  if (selectedDate) {
    const now = moment();
    const timeslots = _.filter(_.find(frames, x => x.date === selectedDate)
      .timeslots, x => moment(x.start).isSameOrAfter(now))
      .sort((x, y) => moment(y.start).isSameOrAfter(moment(x.start)) ? -1 : 1);

    return timeslots.map((x) => {
      const availability = capacityType == 2 ? x.resource_availability : maxAvailability - _.sumBy(x.availability, 'bookings');
      const message = formatAvailabilityMessage(availability, totalQuantity);

      return {
        label: `${formatHours(x.start, locale, timeZone)} - ${formatHours(x.finish, locale, timeZone)} / ${message}`,
        value: x.start,
      };
    });
  }
  return [];
};

export default class ActivityPage extends Component {
  state = {
    currentMonth: null,
    availableDates: [],
    selectOptions: [],
    width: window.innerWidth,
  };

  updateDimensions = this.updateDimensions.bind(this);

  onMonthChange = this.onMonthChange.bind(this);

  onDayClick = this.onDayClick.bind(this);

  dayDisabled = this.dayDisabled.bind(this);

  fakeDisabled = this.fakeDisabled.bind(this);

  goNextMonthButton = this.goNextMonthButton.bind(this);

  onSelectChange = this.onSelectChange.bind(this);

  UNSAFE_componentWillReceiveProps(newProps) {
    const {
      frames,
      capacityType,
      totalQuantity,
      selectedDate,
      selectedLocation,
      locale,
      timeZone,
      maxAvailability,
      minDate,
      maxDate,
      orderActions: { setOrderDetails },
      frameActions: { fetchFrames },
      exceededTrialPlan
    } = newProps;

    const { currentMonth } = this.state,
          currentSelectedLocation = this.props.selectedLocation || {},
          nextSelectedLocation = newProps.selectedLocation || {};

    if (!_.isEqual(nextSelectedLocation, currentSelectedLocation)) {
      if (!exceededTrialPlan) {
        const { start, finish } = getTimeFrameFromDate(this.state.currentMonth, timeZone);
        fetchFrames({ start: start.format(), finish: finish.format(), location_id: nextSelectedLocation.id});
      }
      setOrderDetails({ date: null, selectedTimeslot: null });
    }

    if (frames.length > 0) {
      const availableDates = parseAvailableDates(frames, totalQuantity, minDate, maxDate, timeZone, nextSelectedLocation.id);
      const selectOptions = parseSelectOptions(frames, selectedDate, maxAvailability, totalQuantity, capacityType, locale, timeZone);

      if (!selectedDate && !currentMonth) {
        const firstBookableDate = firstSelectableDate(frames, availableDates);
        if (firstBookableDate) {
          setOrderDetails({ date: firstBookableDate });

          const today = moment().format('YYYY-MM');
          const cm = moment(firstBookableDate).format('YYYY-MM');
          if (today === cm) {
            // if this month is same with first bookable date's month, doesn't load frames.
            this.setState({
              currentMonth: moment(firstBookableDate).format(SHORT_DATE_FORMAT),
            });
          } else {
            // if this month is different with first bookable date's month, go to first bookable date's month.
            this.onMonthChange(firstBookableDate);
          }
        }
      } else {
        this.setState({
          availableDates,
          selectOptions,
        });
      }
    } else {
      this.setState({
        availableDates: [],
        selectOptions: [],
      });
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  updateDimensions() {
    this.setState({
      width: window.innerWidth,
    });
  }

  onSelectVariant(name, value) {
    const {
      orderActions: { setVariantsQuantity },
    } = this.props;
    setVariantsQuantity(name, value);
  }

  onDayClick(day, modifiers = {}) {
    if (modifiers.disabled) return;
    const {
      orderActions: { setOrderDetails, resetVariantsQuantity },
      timeZone,
      frames,
      capacityType,
      selectedDate,
      totalQuantity,
      maxAvailability,
      hasSeasonalRates,
      locale,
    } = this.props;
    const newSelection = moment(day).format(SHORT_DATE_FORMAT);
    if (newSelection !== selectedDate) {
      setOrderDetails({
        date: newSelection,
        selectedTimeslot: null,
      });
    }

    if (hasSeasonalRates) {
      // set quantity to 0 for variants that are related with another Seasonal Rate
      resetVariantsQuantity();
    }

    const selectOptions = parseSelectOptions(frames, newSelection, maxAvailability, totalQuantity, capacityType, locale, timeZone);
    this.setState({
      selectOptions,
    });
  }

  onSelectChange(value) {
    const {
      orderActions: { setOrderDetails },
      frames,
      maxAvailability,
      selectedDate,
    } = this.props;
    if (value && selectedDate) {
      const frame = frames.find(x => x.date === selectedDate);
      const timeslot = frame.timeslots.find(x => x.start === value);
      if (timeslot) {
        const bookingCount = _.sumBy(timeslot.availability, 'bookings');
        const available = this.props.capacityType == 2 ? timeslot.resource_availability : maxAvailability - bookingCount;

        setOrderDetails({
          date: frame.date,
          available: available,
          selectedTimeslot: value,
        });
      }
    }
  }

  onSubmit(e) {
    const {
      selectedDate,
      selectedTimeslot,
      totalQuantity,
      orderActions,
      cartActions,
      additionalDetails,
      bookingFields,
      setting
    } = this.props;
    const ele = e.target,
          messages = [],
          showBookingFields = stringToBoolean(setting.show_booking_fields);

    if (totalQuantity <= 0) {
      messages.push('PLEASE SELECT A VARIANT');
    } else if (!selectedTimeslot) {
      messages.push('PLEASE SELECT A OPTION');
    } else if (!selectedDate) {
      messages.push('PLEASE SELECT A DATE');
    } else if (showBookingFields) {
      const errors = this.validateAdditionalDetails()
      if (errors.length > 0) messages.push('PLEASE SET REQUIRED FIELDS')
    }

    if (!messages.length) {
      ele.disabled = true;
      orderActions
        .submitOrder()
        .then((response) => {
          const { reservation } = response.data;
          if (reservation.items.some(x => x.status !== 'ok')) {
            let m = 'SCHEDULE CONFLICT PLEASE RELOAD PAGE';

            if (window.screen.width < 380) m = 'PLEASE RELOAD PAGE';
            this.alert.show('red', [m]);
            ele.innerText = m;
          } else {
            const fieldsSetting = {additionalDetails, bookingFields}

            cartActions.addItems(reservation, ['Date', 'Time', 'Location'], fieldsSetting);
          }
        })
        .catch((responsed) => {
          ele.disabled = false;
          const { response } = responsed;
          if (response && response.data) {
            const { data } = response;

            Object.keys(data).forEach((key) => {
              const name = key
                .split('_')
                .map(x => capitalize(x))
                .join('');
              messages.push(`${name} ${data[key]}`);
            });

            this.alert.show('red', ['SERVER ERROR']);
          } else {
            this.alert.show('red', ['SOMETHING WRONG']);
            throw responsed;
          }
        });
    } else {
      this.alert.show('red', messages);
    }
  }

  onMonthChange(value) {
    const {
      frameActions: { fetchFrames },
      exceededTrialPlan,
      timeZone,
    } = this.props;

    const selectedLocation = this.props.selectedLocation || {};

    this.setState(
      {
        currentMonth: moment(value).format(SHORT_DATE_FORMAT),
      },
      () => {
        if (!exceededTrialPlan) {
          const { start, finish } = getTimeFrameFromDate(moment(value).format(SHORT_DATE_FORMAT), timeZone);
          fetchFrames({ start: start.format(), finish: finish.format(), location_id: selectedLocation.id });
        }
      },
    );
  }

  onlyDefaultTitle() {
    const { variants } = this.props;
    return variants && variants.length === 1 && variants[0].title === 'Default Title';
  }

  dayDisabled(value) {
    const day = moment(value).format(SHORT_DATE_FORMAT);
    const { availableDates } = this.state;
    if (!availableDates.includes(day)) {
      return new Date(day);
    }
    return null;
  }

  fakeDisabled(value) {
    const { frames } = this.props;
    const day = moment(value).format(SHORT_DATE_FORMAT);
    if (frames.some(frame => frame.date === day)) {
      return new Date(day);
    }
    return null;
  }

  columns() {
    const {
      variants, productOptions, translation, defaultTranslation,
    } = this.props;
    const priceSymbol = translation.priceSymbol || defaultTranslation.priceSymbol;
    const onlyDefaultTitle = this.onlyDefaultTitle();
    const headers = productOptions ? productOptions.map(x => x.name) : [];
    if (!onlyDefaultTitle) {
      headers.push('Price');
    }

    // prepare the list from variant.options and according headers ordering
    const lists = this.variantsFilteredBySeason().map((variant) => {
        const hash = { key: variant.external_id };
        const extra = Array.from(headers).slice(variant.options.length);

        variant.options.filter(x => x).forEach((option, index) => (hash[headers[index]] = option));

        extra.map(x => (hash[x] = formatPrice(variant[x.toLowerCase()], priceSymbol)));
        return hash;
      });

    if (onlyDefaultTitle) lists[0].Title = 'Quantity';

    return { headers, lists };
  }

  variantsFilteredBySeason() {
    const {
      variants, hasSeasonalRates, seasonalRates, selectedDate
    } = this.props,
      selectedDateObj = selectedDate ? moment(selectedDate).toDate() : null

    if (!variants || !hasSeasonalRates) return variants || [];
    if (!selectedDateObj) return []; // Do not display variant if date is not selected
    let filteredSeasonalRates = seasonalRates.filter(seasonalRate => {
      const srStart = moment(seasonalRate.starts_at).toDate();
      const srFinish = moment(seasonalRate.finishes_at).toDate();
      return selectedDateObj >= srStart && selectedDateObj <= srFinish;
    });
    return variants.filter(variant => {
      return filteredSeasonalRates.some(seasonalRate => {
        return seasonalRate.season == variant.options[0] && seasonalRate.rate == variant.options[1];
      });
    });
  }

  extraColumns() {
    const {
      spinner,
      props: { variants, selectedTimeslot, frames },
    } = this;
    const attrs = {};

    if (!variants) return

    variants.forEach((variant) => {
      attrs[variant.external_id] = this.onlyDefaultTitle() ? { style: { float: 'right' } } : {};

      if (selectedTimeslot) {
        const { available } = frames
          .reduce((all, _frame) => all.concat(_frame.timeslots), [])
          .find(slot => slot.start === selectedTimeslot)
          .availability.find(ava => ava.external_id === variant.external_id);

        attrs[variant.external_id] = { ...attrs[variant.external_id], max: available };
      }
    });

    return [
      {
        attrs,
        header: 'quantity',
        type: 'number',
        onChange: ({ target: { name, value } }) => {
          if (spinner) spinner.show();
          this.onSelectVariant(name, parseInt(value, 10));
        },
      },
    ];
  }

  goNextMonthButton() {
    const {
      props: { nextOccurrence },
    } = this;
    if (nextOccurrence) {
      this.setState({
        currentMonth: moment(nextOccurrence).format(SHORT_DATE_FORMAT),
      });
    }
  }

  goConfirmationStep() {
    const {valid, message} = this.validateGoToConfirmation();
    valid ? this.openModal() : this.alert.show('red', [message]);
  }

  validateGoToConfirmation() {
    const { selectedLocation, selectedDate, locations, setting } = this.props,
          showLocation = stringToBoolean(setting.show_location_chooser),
          enableLocationDropdown = showLocation && locations.length > 0;

    let valid = true,
        message = '';

    if (!selectedDate) {
      valid = false;
      message += "PLEASE SELECT DATE"
    } else if (enableLocationDropdown && !selectedLocation) {
      valid = false;
      message += "PLEASE SELECT LOCATION"
    }

    return {valid: valid, message: message}
  }

  render() {
    const {
      timeZone,
      lastOccurrence,
      nextOccurrence,
      totalPrice,
      frames,
      selectedTimeslot,
      variants,
      locations,
      region,
      selectedDate,
      totalQuantity,
      available,
      orderVariants,
      translation,
      style,
      setting,
      exceededTrialPlan,
      orderActions
    } = this.props;

    const { currentMonth, selectOptions, width } = this.state,
          firstOccurrence = _.min(frames.map(x => moment(x.date))),
          selectedDateObj = selectedDate ? moment(selectedDate).toDate() : null,
          showBookingFields = stringToBoolean(setting.show_booking_fields),
          showLocationChooser = stringToBoolean(setting.show_location_chooser),
          enableLocationDropdown = showLocationChooser && locations.length > 0;

    let showGoNextMonthButton = false;

    if (currentMonth) {
      const date = moment(currentMonth);
      showGoNextMonthButton = nextOccurrence
        && frames.filter(x => moment(x.date).format('YYYY-MM') === date.format('YYYY-MM')).length === 0
        && date.isBefore(moment(nextOccurrence));
    }

    const canBook = available >= totalQuantity && frames.length && variants.length,
          t = new Translation(this.props),
          submitText = canBook ? t.key('submit') : t.key('noAvailability'),
          defaultWidth = '450px',
          isLargeScreen = width > 800,
          wrapperStyle = { ...style.body, minHeight: '420px' },
          columns = this.columns(),
          format = convertDateFormat() + ' [at] h:mm a';

    if (!isLargeScreen) wrapperStyle.maxWidth = defaultWidth;

    return (
      <div className="text-center center" style={wrapperStyle}>
        <div className="sub-panel">
          <h3 className="activity-title" style={{...style.title, borderBottom: '1px solid'}}>
            {t.key('title')}
          </h3>

          <p className="text-left">{t.key('subTitle')}</p>

          {exceededTrialPlan &&
            <div className="text-left">
              <Alert unclosed={true} messages={[<ExceededMessage/>]} type="exceeded-trial-plan" />
            </div>
          }

          {enableLocationDropdown &&
            <div className='select-block text-left'>
              <span className='titles' style={{...style.title}}> Location </span>
              <LocationSelect className="mt10" />
            </div>
          }

          <div className="relative" style={{ paddingBottom: '16px'}}>
            <Spinner ref={ref => (this.spinner = ref)} />

            <DatePicker
              fixedWeeks
              styled={{...style.datepicker, ...style.body}}
              region={region}
              className={isLargeScreen ? 'DayPicker--moved-left' : ''}
              fromMonth={moment(firstOccurrence).toDate()}
              toMonth={moment(lastOccurrence).toDate()}
              month={moment(currentMonth || new Date()).toDate()}
              onMonthChange={this.onMonthChange}
              onDayClick={this.onDayClick}
              modifiers={{
                selected: selectedDateObj,
                disabled: this.dayDisabled,
                fakeDisabled: this.fakeDisabled,
              }}
            />

            <div className={isLargeScreen ? 'Select--moved-right' : ''}>
              {showGoNextMonthButton ? (
                <Button
                  className="go-new-month"
                  reverse
                  styled={style.submit}
                  style={{
                    fontSize: '14px',
                    paddingTop: '9px',
                    paddingBottom: '9px',
                  }}
                  onClick={this.goNextMonthButton}
                >
                  {t.key('goNextMonth')}
                </Button>
              ) : (
                <React.Fragment>
                  <p className="text-left bold">Time & spaces available</p>
                  <Select
                    className="Select-dropup"
                    isClearable={false}
                    styles={customSelectStyles(style.select)}
                    isSearchable={false}
                    backspaceRemovesValue={false}
                    value={selectedTimeslot}
                    options={selectOptions}
                    onChange={this.onSelectChange}
                    menuPlacement="auto"
                  />
                </React.Fragment>
              )}

              <TimeZone timeZone={timeZone}/>
            </div>
            <Alert ref={ref => (this.alert = ref)} />
          </div>
          <Button
            styled={style.submit}
            disabled={!canBook}
            className="submit ptb15"
            onClick={this.goConfirmationStep.bind(this)}>
            Next
          </Button>
        </div>

        <Modal
          fullPage
          closeWord={' '}
          getOpenFn={openFn => {this.openModal = openFn;}}
          getCloseFn={closeFn => {this.closeModal = closeFn;}}
        >
          <div className="text-center center">
            <div className="sub-panel relative">
              <button className="btn-back-title" style={style.body} onClick={this.closeModal}>
                <i className="fa fa-arrow-left fa-2" aria-hidden="true"></i>
              </button>

              <h3 className="activity-title text-center" style={{...style.title, borderBottom: '1px solid'}}>
                {t.key('modalTitle')}
              </h3>

              <p className="text-left">{t.key('modalSubTitle')}</p>

              <p className="text-left bold">{moment(selectedTimeslot).tz(timeZone).format(format)}</p>

              <div className="relative">
                <div className="variants-container" style={{ paddingBottom: '16px', marginBottom: '16px', borderBottom: '1px solid'}}>
                  {columns.lists.length > 0 &&
                    this.props.hasSeasonalRates ?
                      <RatesTable
                        style={style}
                        items={orderVariants}
                        columns={columns}
                        extraColumns={this.extraColumns()}
                      />
                    :
                      <Table
                        className="table-component"
                        hideHead={this.onlyDefaultTitle()}
                        style={style.variantsTable}
                        items={orderVariants}
                        columns={columns}
                        extraColumns={this.extraColumns()}
                      />
                  }

                  {columns.lists.length <= 0 && (
                    <div style={{textAlign: 'left'}}>
                      {translation.noAvailability}
                    </div>
                  )}
                </div>

                {showBookingFields &&
                  <BookingFieldsBlock
                    getValidationFn={validationFn => {this.validateAdditionalDetails = validationFn;}}
                    bookingFields={this.props.bookingFields}
                    additionalDetails={this.props.additionalDetails}
                    onChange={orderActions.changeAdditionalDetails}
                    onAddRepeatedField={orderActions.onAddRepeatedFieldItems}
                    onRemoveRepeatedFields={orderActions.onRemoveRepeatedFieldItems}
                    onSetErrors={orderActions.onSetDetailsErrors}
                    errors={this.props.additionalDetailsValidationErrors}
                    style={style}
                  />
                }

                <Price title="Total price:"/>

                <Alert ref={ref => (this.alert = ref)} />
              </div>

              <Button
                styled={style.submit}
                disabled={!canBook}
                className="submit ptb15"
                onClick={e => this.onSubmit(e)}>
                {submitText}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

ActivityPage.propTypes = {
  translation: PropTypes.object,
  defaultTranslation: PropTypes.object,
  timeZone: PropTypes.string,
  productOptions: PropTypes.arrayOf(PropTypes.shape({
    position: PropTypes.number,
    name: PropTypes.string,
    values: PropTypes.array,
  })),
  variants: PropTypes.array,
  available: PropTypes.number,
  bookingFields: PropTypes.array,
  additionalDetails: PropTypes.object,
  frames: PropTypes.array,
  totalPrice: PropTypes.number,
  totalQuantity: PropTypes.number,
  nextOccurrence: PropTypes.string,
  lastOccurrence: PropTypes.instanceOf(Date),
  selectedDate: PropTypes.string,
  selectedTimeslot: PropTypes.string,
  orderActions: PropTypes.shape({
    setVariantsQuantity: PropTypes.func,
    setSelectedTimeslot: PropTypes.func,
    setOrderDetails: PropTypes.func,
    changeAdditionalDetails: PropTypes.func,
  }),
  cartActions: PropTypes.shape({
    addItems: PropTypes.func,
  }),
  frameActions: PropTypes.shape({ fetchFrames: PropTypes.func }),
  orderVariants: PropTypes.shape({
    external_id: PropTypes.string,
    quantity: PropTypes.number,
    start: PropTypes.instanceOf(Date),
  }),
  style: PropTypes.object,
};
